// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { color, ellipsis, hideElement, mergeWithCss, xClassNames, xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler } from "react";
import { useState } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    variant?: "link" | "cart";
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    subTitle?: string;
    isOpen?: boolean;
    extendedStyles?: HeaderClickableStyle;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
    cartWrapper?: InjectableCss;
}

export const headerClickableStyles: HeaderClickableStyle = {};

const HeaderClickable: FC<Props> = ({
    variant,
    iconProps,
    titleProps,
    title,
    subTitle,
    extendedStyles,
    isOpen,
    children,
    ...otherProps
}) => {
    const { isTabletScreen, isSticky } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isSticky: (state as CustomAppState).xHeader.isSticky,
    }));
    const classNames = xClassNames.append([isTabletScreen && "mobile-mode", isOpen && "opened"], otherProps.className);
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    if (variant === "cart") {
        return (
            // XNG-CHNAGES GSS-125 : Removing cart aria-label since it affects accessibility.
            <Clickable {...styles.clickable} {...otherProps} className={classNames}>
                <StyledWrapper {...styles.cartWrapper}>
                    {children}
                    {(!isTabletScreen || (isTabletScreen && !children)) && iconProps && (
                        <Icon {...styles.icon} {...iconProps} size={isTabletScreen ? 22 : 30} className="menu-icon" />
                    )}
                </StyledWrapper>
                <Typography {...styles.title} {...titleProps} className={xClassNames.append([isSticky && "sticky"])}>
                    {title && <Typography>{title}</Typography>}
                </Typography>
            </Clickable>
        );
    }

    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames} aria-label={title}>
            {iconProps && (
                <Icon {...styles.icon} {...iconProps} size={isTabletScreen ? 22 : 26} className="menu-icon" />
            )}
            <Typography {...styles.title} {...titleProps} className={xClassNames.append([isSticky && "sticky"])}>
                {title && <Typography>{title}</Typography>}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
